<template>
  <v-container>
    <v-card v-if="$route.name === 'access-code-set'">
      <v-toolbar>
        <v-toolbar-title>{{ t("access_code_set.list.title") }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          :to="{ name: 'access-code-set-edit', params: { id: 'create' } }"
        >
          <template v-slot:prepend>
            <v-icon>mdi-plus</v-icon>
          </template>
          {{ t("generic.btn_create") }}
        </v-btn>
      </v-toolbar>

      <v-container>
        <v-data-table
          :items="rows"
          :items-per-page="pager.entries_per_page"
          :items-length="pager.total_entries"
          :headers="headers"
          hide-default-footer
        >
          <template v-slot:item="{ item }">
            <tr>
              <td>{{ item.name }}<br />{{ item.internal_name }}</td>
              <td>{{ t("access_code_set.list.type__" + item.type) }}</td>
              <td>
                {{ formatDatetime(item.modified) }}<br />
                {{ item.email }}
              </td>
              <td>
                {{ formatDate(item.valid_from) }}<br />
                {{ formatDate(item.valid_until) }}
              </td>
              <td>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn
                      variant="text"
                      icon
                      v-bind="tooltipProps"
                      :to="{
                        name: 'access-code-set-edit',
                        params: { id: item.id },
                      }"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t("generic.btn_edit") }}</span>
                </v-tooltip>
                <v-tooltip location="bottom">
                  <template v-slot:activator="{ props: tooltipProps }">
                    <v-btn
                      variant="text"
                      icon
                      v-bind="tooltipProps"
                      :to="{
                        name: 'access-code-set-usage',
                        params: { ident: item.id },
                      }"
                    >
                      <v-icon>mdi-chart-bar</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ t("access_code_set.list.btn_usage") }}</span>
                </v-tooltip>
              </td>
            </tr>
          </template>
          <template v-slot:bottom>
            <div class="text-right pa-2">
              <span class="pa-2">{{
                t("generic.pager_total_hits", { hits: pager.total_entries })
              }}</span>
              <v-btn
                v-if="prevPage"
                @click="page = prevPage.page"
                icon
                size="small"
                class="ma-2"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <v-btn
                v-if="nextPage"
                @click="page = nextPage.page"
                icon
                size="small"
                class="ma-2"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-container>
    </v-card>
    <v-container v-else>
      <router-view></router-view>
    </v-container>
  </v-container>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { apiAccessCodeSetList } from '@/api/access-code-set.js'
import { formatDate, formatDatetime } from '@/utils/format.js'

const { t } = useI18n()

const rows = ref([])
const page = ref(1)
const pager = ref({
  links: [],
  entries_per_page: 10,
  total_entries: 0,
})

const headers = computed(() => [
  { key: 'name', title: t('access_code_set.list.th_name'), sortable: false },
  { key: 'type', title: t('access_code_set.list.th_type'), sortable: false },
  { key: 'modified', title: t('access_code_set.list.th_modified'), sortable: false },
  { key: 'validity', title: t('access_code_set.list.th_validity'), sortable: false },
  { key: 'actions', title: t('access_code_set.list.th_actions'), sortable: false },
])

const nextPage = computed(() => pager.value.links.find((l) => l.rel === 'next'))
const prevPage = computed(() => pager.value.links.find((l) => l.rel === 'prev'))

watch(page, () => {
  fetchData()
})

onMounted(() => {
  fetchData()
})

const fetchData = async () => {
  const res = await apiAccessCodeSetList({ page: page.value })
  if (res) {
    rows.value = res.rows
    pager.value = res.pager
  }
}
</script>

<style scoped>
:deep(td) {
  vertical-align: top;
}
</style>
